import { HelpCircle } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router';

/**
 * Kleiner Hilfe-Knopf, der direkt die passende Anleitung öffnet (/anleitungen?anleitung=<id>,
 * wird dort von Anleitungen.tsx ausgewertet). Bewusst ohne Tooltip-Bibliothek — das native
 * `title`-Attribut reicht für "Anleitung öffnen" und ist überall ohne weitere Abhängigkeit
 * verfügbar.
 *
 * Hängt die aktuelle Seite (Pfad + Query, z.B. "/customers?customer=42") als `zurueck`-
 * Parameter an — die Anleitungen-Seite zeigt daraus im Lesefenster einen Rücksprung-Button,
 * der genau dorthin zurückführt (samt Kunden-/Berichtskontext), statt nur zur allgemeinen
 * Übersicht. Gilt nur für diesen einen Aufruf, wird nirgends gespeichert.
 *
 * `variant="toolbar"` passt sich an eine Reihe quadratischer Symbol-Knöpfe an (z. B. neben
 * "Neuer Bericht"), `variant="icon"` (Standard) ist der kleine, zurückhaltende Knopf für
 * Platz direkt neben einem Titel oder Feld.
 */
export function HilfeLink({
  anleitungId,
  label = 'Anleitung öffnen',
  variant = 'icon',
  className = '',
}: {
  anleitungId: string;
  label?: string;
  variant?: 'icon' | 'toolbar';
  className?: string;
}) {
  const navigate = useNavigate();
  const ort = useLocation();
  const basisKlasse =
    variant === 'toolbar'
      ? 'w-12 h-12 aspect-square flex items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors shadow-sm hover:shadow-md'
      : 'p-0.5 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-200 dark:hover:text-slate-200 dark:hover:bg-slate-700 transition-colors';

  const oeffnen = () => {
    const params = new URLSearchParams();
    params.set('anleitung', anleitungId);
    params.set('zurueck', `${ort.pathname}${ort.search}`);
    navigate(`/anleitungen?${params.toString()}`);
  };

  return (
    <button
      type="button"
      onClick={oeffnen}
      title={label}
      aria-label={label}
      className={`${basisKlasse} ${className} cursor-pointer`}
    >
      <HelpCircle className={variant === 'toolbar' ? 'w-5 h-5' : 'w-3.5 h-3.5'} />
    </button>
  );
}
